import { useCallback, useMemo } from "react";
import { useMistralNarrativeOverlay } from "@/hooks/useMistralNarrativeOverlay";
import { bulletsToActionQueue, bulletsToDropboxNarratives } from "@/utils/mistralInsightMappers";
import {
  Award,
  BookOpen,
  Users,
  Monitor,
  AlertTriangle,
  ShieldCheck,
} from "lucide-react";
import {
  buildQualityTqiGaugeStyle,
  buildQualityHeroHeadline,
  buildQualityHeroNarrative,
  buildQualityStatusChips,
  buildQualityPrimaryAction,
  buildQualityKpis,
  buildDomainCards,
  buildCoverageRows,
  buildCoverageInsights,
  buildQualificationRiskRows,
  buildTqiLeaderboard,
  buildInterventionQueue,
  buildQualityActionCards,
  buildReviewCadenceNote,
  buildTeacherQualityMarkdown,
} from "@/utils/teacherQualityDerived";
import { toTitleCase } from "../utils/toTitleCase";

const DOMAIN_ICONS = {
  "": Award,
  purple: Users,
  green: Monitor,
};

const ACTION_ICONS = {
  red: AlertTriangle,
  purple: Users,
  green: Monitor,
};

const scrollToId = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const TeacherQualityTab = ({
  overview = {},
  trainingCoverage = [],
  qualificationRisk = [],
  blockData = [],
}) => {
  const tqi = overview.teacher_quality_index ?? 0;
  const hasData = Boolean(overview?.teachers_cy);

  const buildLocalMarkdown = useCallback(
    () => buildTeacherQualityMarkdown(overview, qualificationRisk),
    [overview, qualificationRisk]
  );
  const scopeKey = useMemo(
    () => JSON.stringify({ t: overview?.teachers_cy, q: tqi }),
    [overview?.teachers_cy, tqi]
  );
  const {
    loading: aiLoading,
    pickHeadline,
    pickFromBucket,
    pickPrimaryAction,
    pickMapped,
    isMistral,
  } = useMistralNarrativeOverlay({
    kind: "teacher-quality",
    title: "Teacher Quality Command Center",
    buildLocalMarkdown,
    context: { overview, qualificationRisk },
    enabled: hasData,
    scopeKey,
  });

  const gaugeStyle = useMemo(() => buildQualityTqiGaugeStyle(tqi), [tqi]);
  const localHeadline = useMemo(() => buildQualityHeroHeadline(overview), [overview]);
  const localNarrative = useMemo(() => buildQualityHeroNarrative(overview), [overview]);
  const headline = useMemo(
    () => (aiLoading ? localHeadline : pickHeadline(localHeadline)),
    [aiLoading, pickHeadline, localHeadline]
  );
  const narrative = useMemo(
    () => (aiLoading ? localNarrative : pickFromBucket(localNarrative, "insights", 1)),
    [aiLoading, pickFromBucket, localNarrative]
  );
  const statusChips = useMemo(() => {
    const chips = buildQualityStatusChips(overview);
    if (isMistral && !aiLoading) {
      return [{ label: "Mistral AI Assisted", className: "purple" }, ...chips];
    }
    return chips;
  }, [overview, isMistral, aiLoading]);
  const localPrimaryAction = useMemo(
    () => buildQualityPrimaryAction(qualificationRisk),
    [qualificationRisk]
  );
  const primaryAction = useMemo(
    () => (aiLoading ? localPrimaryAction : pickPrimaryAction(localPrimaryAction)),
    [aiLoading, pickPrimaryAction, localPrimaryAction]
  );
  const kpis = useMemo(() => buildQualityKpis(overview), [overview]);
  const domainCards = useMemo(() => buildDomainCards(overview), [overview]);
  const coverageRows = useMemo(
    () => buildCoverageRows(overview, trainingCoverage),
    [overview, trainingCoverage]
  );
  const localCoverageInsights = useMemo(() => buildCoverageInsights(overview), [overview]);
  const coverageInsights = useMemo(
    () => pickMapped(localCoverageInsights, "insights", bulletsToDropboxNarratives),
    [pickMapped, localCoverageInsights]
  );
  const riskRows = useMemo(
    () => buildQualificationRiskRows(qualificationRisk),
    [qualificationRisk]
  );
  const leaderboard = useMemo(() => buildTqiLeaderboard(blockData), [blockData]);
  const interventionQueue = useMemo(
    () => buildInterventionQueue(qualificationRisk, blockData),
    [qualificationRisk, blockData]
  );
  const localActionCards = useMemo(
    () => buildQualityActionCards(qualificationRisk),
    [qualificationRisk]
  );
  const actionCards = useMemo(
    () => pickMapped(localActionCards, "recs", bulletsToActionQueue),
    [pickMapped, localActionCards]
  );
  const localReviewNote = useMemo(() => buildReviewCadenceNote(overview), [overview]);
  const reviewNote = useMemo(
    () => (aiLoading ? localReviewNote : pickFromBucket(localReviewNote, "actions", 0)),
    [aiLoading, pickFromBucket, localReviewNote]
  );

  return (
    <div className="tqt-quality-tab" data-testid="teacher-quality">
      <section className="tqt-hero-card" id="tqt-hero">
        <div className="tqt-hero-layout">
          <div>
            <div className="tqt-quality-main">
              <div
                className="tqt-gauge"
                style={gaugeStyle}
                aria-label={`Teacher quality index ${tqi}`}
              >
                <div>
                  <strong>{tqi}</strong>
                  <span>TQI Score</span>
                </div>
              </div>
              <div className="tqt-quality-copy">
                <h2 className="dashboard-kpi-hero-title">{headline}</h2>
                <p>{narrative}</p>
                <div className="tqt-status-row">
                  {statusChips.map((chip) => (
                    <span
                      key={chip.label}
                      className={`tqt-status-chip ${chip.className}`.trim()}
                    >
                      {chip.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="tqt-summary-note">
              <div className="tqt-icon-box purple">
                <BookOpen size={22} />
              </div>
              <div>
                <b>Primary executive action</b>
                <span>{primaryAction}</span>
              </div>
            </div>
          </div>

          <div className="tqt-metric-grid" aria-label="Quality and training KPIs">
            {kpis.map((card) => (
              <article key={card.label} className={`tqt-metric-card ${card.cardClass}`.trim()}>
                <small>{card.label}</small>
                <strong>{card.value}</strong>
                <span>{card.sub}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className="tqt-section-title" id="tqt-coverage">
        <div>
          <h2>Training Coverage Cockpit</h2>
          <p>
            Training metrics are grouped by decision theme instead of showing isolated cards, so
            officers can quickly see what is ready, what is weak, and what needs action.
          </p>
        </div>
        <button
          type="button"
          className="tqt-section-action"
          onClick={() => scrollToId("tqt-actions")}
        >
          View action queue →
        </button>
      </div>

      <section className="tqt-domain-grid">
        {domainCards.map((card) => {
          const Icon = DOMAIN_ICONS[card.iconClass] ?? Award;
          return (
            <article key={card.id} className="tqt-domain-card">
              <div className="tqt-domain-head">
                <div className={`tqt-icon-box ${card.iconClass}`.trim()}>
                  <Icon size={22} />
                </div>
                <span className={`tqt-pill ${card.badgeClass}`.trim()}>{card.badge}</span>
              </div>
              <h3>{toTitleCase(card.title)}</h3>
              <p>{card.body}</p>
              <div className="tqt-domain-score">
                <strong>{card.score}</strong>
                <span>{card.scoreLabel}</span>
              </div>
              <div className="tqt-progress-line">
                <i style={{ width: `${card.width}%`, background: card.barColor }} />
              </div>
              <div className="tqt-domain-meta">
                <span>{card.metaLeft}</span>
                <span>{card.metaRight}</span>
              </div>
            </article>
          );
        })}
      </section>

      <div className="tqt-section-title" id="tqt-diagnostics">
        <div>
          <h2>Risk Diagnostics</h2>
          <p>
            Progress bars and block-risk ranking replace dense chart blocks, making the tab easier
            to scan during district reviews.
          </p>
        </div>
        <button
          type="button"
          className="tqt-section-action"
          onClick={() => scrollToId("tqt-leaderboard")}
        >
          Go to leaderboard →
        </button>
      </div>

      <section className="tqt-analytics-grid">
        <article className="tqt-panel">
          <div className="tqt-panel-head">
            <div>
              <h3>Training Coverage Overview</h3>
              <p>Coverage against total teacher base. Targets can be configured by district or state policy.</p>
            </div>
            <span className="tqt-pill">Coverage %</span>
          </div>
          <div className="tqt-coverage-list" aria-label="Training coverage progress list">
            {coverageRows.map((row) => (
              <div key={row.label} className="tqt-coverage-row">
                <div className="tqt-coverage-label">
                  <b>{row.label}</b>
                  <span>{row.sub}</span>
                </div>
                <div className="tqt-coverage-track">
                  <i className={row.barClass} style={{ width: `${row.width}%` }} />
                </div>
                <div className="tqt-coverage-value">{row.value}</div>
              </div>
            ))}
          </div>
          <div className="tqt-target-note">
            {coverageInsights.map((note) => (
              <div key={note.title} className="tqt-mini-note">
                <b>{note.title}</b>
                <span>{note.body}</span>
              </div>
            ))}
          </div>
        </article>

        <article className="tqt-panel">
          <div className="tqt-panel-head">
            <div>
              <h3>Qualification Risk by Block</h3>
              <p>Teachers below graduation level. Prioritize the highest-volume blocks first.</p>
            </div>
            <span className="tqt-pill red">Top risk</span>
          </div>
          <div className="tqt-risk-chart" aria-label="Qualification risk by block">
            {riskRows.map((row) => (
              <div key={row.block} className="tqt-risk-row">
                <div className="tqt-risk-label">{row.block}</div>
                <div className="tqt-risk-track">
                  <div className="tqt-risk-bar" style={{ width: `${row.width}%` }} />
                </div>
                <div className="tqt-risk-value">{row.value}</div>
              </div>
            ))}
          </div>
        </article>
      </section>

      <div className="tqt-section-title" id="tqt-leaderboard">
        <div>
          <h2>Block-Wise Teacher Quality Index</h2>
          <p>
            Leaderboard is separated from coverage analytics, so users can compare blocks without
            mixing risk volume and quality scores.
          </p>
        </div>
        <button
          type="button"
          className="tqt-section-action"
          onClick={() => scrollToId("tqt-actions")}
        >
          Review interventions →
        </button>
      </div>

      <section className="tqt-panel tqt-leader-card">
        <div className="tqt-leader-head">
          <div>
            <h3>TQI Leaderboard</h3>
            <p>
              TQI = CTET × 0.4 + CWSN × 0.3 + Computer × 0.3. Values shown as indicative scores
              for redesigned view.
            </p>
          </div>
          <span className="tqt-pill purple">Composite index</span>
        </div>
        <div className="tqt-leader-body">
          <div className="tqt-leader-grid" aria-label="Block-wise teacher quality index leaderboard">
            {leaderboard.map((row) => (
              <div key={row.block} className="tqt-leader-row">
                <div className="tqt-leader-block">
                  <span className="tqt-rank">{row.rank}</span>
                  {row.block}
                </div>
                <div className="tqt-leader-track">
                  <div
                    className={`tqt-leader-bar ${row.barClass}`.trim()}
                    style={{ width: `${row.width}%` }}
                  />
                </div>
                <div className="tqt-leader-value">{row.display}</div>
              </div>
            ))}
          </div>
          <div className="tqt-legend-row">
            <span><i className="tqt-legend-dot" />Leading blocks</span>
            <span><i className="tqt-legend-dot blue" />Middle band</span>
            <span><i className="tqt-legend-dot orange" />Needs support</span>
          </div>
        </div>
      </section>

      <div className="tqt-section-title" id="tqt-actions">
        <div>
          <h2>Action and Accountability</h2>
          <p>
            The bottom section converts the analytics into a district review agenda with clear
            owners, urgency and intervention themes.
          </p>
        </div>
        <button
          type="button"
          className="tqt-section-action"
          onClick={() => scrollToId("tqt-hero")}
        >
          Back to top ↑
        </button>
      </div>

      <section className="tqt-two-column">
        <article className="tqt-panel tqt-table-card">
          <div className="tqt-table-head">
            <div>
              <h3>Priority Intervention Queue</h3>
              <p>High-risk blocks and recommended training actions.</p>
            </div>
            <span className="tqt-pill red">Next 30 days</span>
          </div>
          <table className="tqt-intervention-table" aria-label="Priority intervention queue">
            <thead>
              <tr>
                <th>Priority</th>
                <th>Block</th>
                <th>Primary Issue</th>
                <th>Risk Volume</th>
                <th>Recommended Action</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {interventionQueue.map((row) => (
                <tr key={row.block}>
                  <td>{row.priority}</td>
                  <td>{row.block}</td>
                  <td>{row.issue}</td>
                  <td className="score">{row.volume}</td>
                  <td>{row.action}</td>
                  <td>
                    <span className={`tqt-tag ${row.statusClass}`.trim()}>{row.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>

        <article className="tqt-panel">
          <div className="tqt-panel-head">
            <div>
              <h3>Recommended Action Cards</h3>
              <p>Three executive-ready actions for the Quality & Training review.</p>
            </div>
            <span className="tqt-pill purple">Priority</span>
          </div>
          <div className="tqt-action-card">
            {actionCards.map((item) => {
              const Icon = ACTION_ICONS[item.iconClass] ?? ShieldCheck;
              return (
                <div key={item.title} className="tqt-action-item">
                  <div className={`tqt-icon-box ${item.iconClass}`.trim()}>
                    <Icon size={22} />
                  </div>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="tqt-action-footer">
            <b>Review cadence recommendation</b>
            <span>{reviewNote}</span>
          </div>
        </article>
      </section>
    </div>
  );
};

export default TeacherQualityTab;
